import { EntitySubtypeName } from "@ds/model/entity-model";

import { DetailsTableDataTypeEnum, MainTableDataTypeEnum } from "@ds/modules/table-data/utils/model";

import { type ProgressInfo } from "@ds/services/storage";

import { assetMediaTypes } from "./constants";
import { ApiContentTypeEnum, ContentTypeNameEnum } from "./model";

export const getSupportedFormatsByTableType = (tableType: MainTableDataTypeEnum) => {
  switch (tableType) {
    case MainTableDataTypeEnum.AssetVideos:
      return assetMediaTypes.video;
    case MainTableDataTypeEnum.AssetImages:
      return assetMediaTypes.image;
    case MainTableDataTypeEnum.AssetAudio:
      return assetMediaTypes.audio;
    default:
      return {};
  }
};

export const getSupportedFormatsByEntityType = (entityType?: EntitySubtypeName) => {
  switch (entityType) {
    case EntitySubtypeName.ASSET_VIDEO:
      return assetMediaTypes.video;
    case EntitySubtypeName.ASSET_IMAGE:
      return assetMediaTypes.image;
    case EntitySubtypeName.ASSET_AUDIO:
      return assetMediaTypes.audio;
    default:
      return {};
  }
};

export const getApiContentTypeByTableType = (tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum) => {
  switch (tableType) {
    case MainTableDataTypeEnum.AssetVideos:
      return ApiContentTypeEnum.Video;
    case MainTableDataTypeEnum.AssetImages:
      return ApiContentTypeEnum.Image;
    case MainTableDataTypeEnum.AssetAudio:
      return ApiContentTypeEnum.Audio;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getApiContentTypeByEntitySubtype = (entityType: EntitySubtypeName) => {
  switch (entityType) {
    case EntitySubtypeName.ASSET_VIDEO:
      return ApiContentTypeEnum.Video;
    case EntitySubtypeName.ASSET_IMAGE:
      return ApiContentTypeEnum.Image;
    case EntitySubtypeName.ASSET_AUDIO:
      return ApiContentTypeEnum.Audio;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getMainTableTypeByApiContentType = (apiContentType?: ApiContentType) => {
  switch (apiContentType) {
    case ApiContentTypeEnum.Video:
      return MainTableDataTypeEnum.AssetVideos;
    case ApiContentTypeEnum.Image:
      return MainTableDataTypeEnum.AssetImages;
    case ApiContentTypeEnum.Audio:
      return MainTableDataTypeEnum.AssetAudio;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getEntitySubtypeByApiContentType = (apiContentType?: ApiContentType) => {
  switch (apiContentType) {
    case ApiContentTypeEnum.Video:
      return EntitySubtypeName.ASSET_VIDEO;
    case ApiContentTypeEnum.Image:
      return EntitySubtypeName.ASSET_IMAGE;
    case ApiContentTypeEnum.Audio:
      return EntitySubtypeName.ASSET_AUDIO;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getMainTableDataTypeByEntitySubtype = (entityType: EntitySubtypeName) => {
  switch (entityType) {
    case EntitySubtypeName.ASSET_VIDEO:
      return MainTableDataTypeEnum.AssetVideos;
    case EntitySubtypeName.ASSET_IMAGE:
      return MainTableDataTypeEnum.AssetImages;
    case EntitySubtypeName.ASSET_AUDIO:
      return MainTableDataTypeEnum.AssetAudio;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getEntitySubtypeByTableType = (tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum) => {
  switch (tableType) {
    case MainTableDataTypeEnum.AssetVideos:
      return EntitySubtypeName.ASSET_VIDEO;
    case MainTableDataTypeEnum.AssetImages:
      return EntitySubtypeName.ASSET_IMAGE;
    case MainTableDataTypeEnum.AssetAudio:
      return EntitySubtypeName.ASSET_AUDIO;
    default:
      throw new Error("Unsupported content type");
  }
};

export const getContentTypeNameByTableType = (tableType: MainTableDataTypeEnum | DetailsTableDataTypeEnum) => {
  switch (tableType) {
    case MainTableDataTypeEnum.AssetVideos:
      return ContentTypeNameEnum.Video;
    case MainTableDataTypeEnum.AssetImages:
      return ContentTypeNameEnum.Image;
    case MainTableDataTypeEnum.AssetAudio:
      return ContentTypeNameEnum.Audio;
    default:
      throw new Error("Unsupported content type");
  }
};

export const createAssetFiltersByEntityType = ({
  entityType,
  name,
}: {
  entityType?: EntitySubtypeName;
  name?: string;
}): KeysToCamelCase<AssetFilters> =>
  entityType ? { contentType: getApiContentTypeByEntitySubtype(entityType), name } : { name };

export const createAssetFiltersByTableType = ({
  tableType,
  name,
}: {
  tableType?: MainTableDataTypeEnum;
  name?: string;
}): KeysToCamelCase<AssetFilters> =>
  tableType ? { contentType: getApiContentTypeByTableType(tableType), name } : { name };

export const getAssetFileNameFromProgressInfo = (progressInfo: ProgressInfo) => {
  let assetFileName = progressInfo.name;
  if (!assetFileName && progressInfo.key) {
    const keyPart = progressInfo.key.substring(progressInfo.key.lastIndexOf("/") + 1);
    assetFileName = keyPart.substring(keyPart.indexOf("_") + 1);
  }

  return assetFileName;
};

export const isContentTypeVideo = (contentType: string) =>
  contentType.toLowerCase().startsWith(ContentTypeNameEnum.Video);

export const isContentTypeImage = (contentType: string) =>
  contentType.toLowerCase().startsWith(ContentTypeNameEnum.Image);

export const isContentTypeAudio = (contentType: string) =>
  contentType.toLowerCase().startsWith(ContentTypeNameEnum.Audio);

export const getMainTableTypeByContentType = (contentType: string) => {
  if (isContentTypeVideo(contentType)) {
    return MainTableDataTypeEnum.AssetVideos;
  }

  if (isContentTypeImage(contentType)) {
    return MainTableDataTypeEnum.AssetImages;
  }

  if (isContentTypeAudio(contentType)) {
    return MainTableDataTypeEnum.AssetAudio;
  }

  throw new Error("Unsupported content type");
};
